/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {FetchScope, query as queryApi} from '@adobe/exc-app/network';
import metrics, {Level} from '@adobe/exc-app/metrics';
import {SettingsLevel} from '@adobe/exc-app/settings';

export const PREF_APPID = 'userPreferences';
export const PREF_GROUPID = 'exc-preferences';

const GQL_FRAGMENTS = {
  extendedAccountData: `
    fragment extendedFragment on ImsExtendedAccountClusterData {
      data {
        orgs {
          imsOrgId
          hasAEP
          tenantId
          orgName
        }
        owningOrg {
          imsOrgId
          hasAEP
          tenantId
          orgName
        }
        userId
        userType
      }
      next
    }
  `
};

export const PREF_PARAMS = `appId: "${PREF_APPID}", groupId: "${PREF_GROUPID}", level: "${SettingsLevel.USER}"`;

export const RELOAD_QUERY = `
  query shellInitDataQuery {
    getSettings(${PREF_PARAMS}, settings: {
      coachMarks: {},
      theme: "",
      userConsent: {},
      userSandboxes: {}
    }) {
      settings
    }
    imsExtendedAccountClusterData {
      ...extendedFragment
    }
  }
  ${GQL_FRAGMENTS.extendedAccountData}
`;

/* eslint-disable max-len */
export const LOGIN_QUERY = `
  query shellInitDataQuery {
    imsExtendedAccountClusterData(useCache: true) {
      ...extendedFragment
    }
    getSettings(${PREF_PARAMS}, settings: {
      coachMarks: {},
      defaultOrg: "",
      lastLoggedInOrg: "",
      theme: "",
      userConsent: {},
      userSandboxes: {}
    }) {
      settings
    }
    userProfileJson(ignoreImsCache: false)
  }
  ${GQL_FRAGMENTS.extendedAccountData}
`;

export const ACCOUNTS_QUERY = `
  query extendedAccountData($from: String!) {
    imsExtendedAccountClusterData(from: $from) {
      ...extendedFragment
    }
  }
  ${GQL_FRAGMENTS.extendedAccountData}
`;

export const SHELL_HEADER_QUERY = `
  query shellHeaderQuery($locale: String, $config_version: Int, $settings: JSON!, $imageKeySize: [Int]!) {
    getSettings(settings: $settings, appId: "pulse", groupId: "notifications") {
      settings
    }
    userBehance {
      images
      encodedImage(imageKey: $imageKeySize)
    }
    userOrgPreferences {
      displayAlert
    }
    timeline(config_version: $config_version, locale: $locale)
    userProfileJson
  }
`;

export const USER_CONSENT_QUERY = `
  query getConsentPermissions {
    getConsentPermissions {
      last_update_dts
      permissions {
        name
        enabled
        last_update_dts
      }
    }
  }
`;

export const SET_USER_CONSENT = `
  mutation setConsentPermissions($source: String!, $permissions: [ConsentPermissionInput!]!) {
    setConsentPermissions(source: $source, permissions: $permissions) {
      success
      errors
    }
  }
`;

export const SANDBOX_QUERY = `
  query getUserSandboxes($fulfillableData: FulfillableData) {
    userSandboxes(fulfillableData: $fulfillableData) {
      title
      region
      name
      state
      isDefault
      type
    }
  }
`;

export const PERMISSIONS_QUERY = `
  query effectivePolicies {
    effectivePolicies {
      policies {
        name
        privileges
      }
    }
  }
`;

export const LAUNCH_DARKLY_QUERY = `
  query imsOrgFeatureFlagSettingsQuery($userData: FeatureFlagsUserInput!) {
    featureFlags(projectId: "unified-shell", user: $userData) {
      user {
        key
        custom {
          locale
          sandboxName
          imsOrgId
        }
      }
      featureFlags {
        name
        value
      }
    }
  }
`;

export const MULTIPLE_PROJECT_FEATURE_FLAGS_QUERY = `
  query multipleProjectFeatureFlagsQuery($projectIds: [String], $userData: FeatureFlagsUserInput!) {
    multiProjectFeatureFlags(projectIds: $projectIds, user: $userData) {
      user {
        key
        custom {
          locale
          sandboxName
          imsOrgId
        }
      }
      projects {
        projectId
        featureFlags {
          name
          value
        }
      }
    }
  }
`;
/* eslint-enable max-len */

const REQ_ERR_MSG = '[GraphQL Service Error in Shell Data Init]';

export class GraphQlService {
  constructor() {
    this.metrics = metrics.create('exc.core.GraphQlService');
  }

  initializeShellDataForLogin(variables) {
    return this.shellDataHandler(LOGIN_QUERY, {
      debugKey: 'User Login'
    })(variables);
  }

  initializeShellDataForReload(variables) {
    return this.shellDataHandler(RELOAD_QUERY, {
      debugKey: 'App Re-load'
    })(variables);
  }

  initializeShellDataForHeader(variables, getProfile) {
    // If we don't need a fresh profile, remove it from the query.
    const query = getProfile ?
      SHELL_HEADER_QUERY :
      SHELL_HEADER_QUERY.replace(/.*userProfileJson.*\n/, '');
    return this.shellDataHandler(query, {
      debugKey: 'Header Data',
      queryName: 'HeaderData'
    })(variables);
  }

  getPaginatedAccountClusters(variables) {
    return this.shellDataHandler(ACCOUNTS_QUERY, {
      debugKey: 'Extended Accounts'
    })(variables);
  }

  getUserConsent() {
    return this.shellDataHandler(USER_CONSENT_QUERY, {
      debugKey: 'Get User Consent',
      queryName: 'GetUserConsent'
    })();
  }

  setUserConsent(variables) {
    return this.shellDataHandler(SET_USER_CONSENT, {
      debugKey: 'Set User Consent',
      queryName: 'SetUserConsent'
    })(variables);
  }

  getUserSandboxes(variables) {
    return this.shellDataHandler(SANDBOX_QUERY, {
      debugKey: 'User Sandboxes',
      queryName: 'UserSandboxes'
    })(variables);
  }

  getUserPermissions(variables) {
    return this.shellDataHandler(PERMISSIONS_QUERY, {
      debugKey: 'User Permissions',
      queryName: 'UserPermissions',
      scope: FetchScope.SANDBOX
    })(variables);
  }

  launchDarklyFeatureFlags(variables) {
    return this.shellDataHandler(LAUNCH_DARKLY_QUERY, {
      debugKey: 'Launch Darkly',
      queryName: 'imsOrgFeatureFlagSettings'
    })(variables);
  }

  getMultiProjectFeatureFlags(variables) {
    return this.shellDataHandler(MULTIPLE_PROJECT_FEATURE_FLAGS_QUERY, {
      debugKey: 'Launch Darkly Multiple Projects',
      queryName: 'multipleProjectFeatureFlags'
    })(variables);
  }

  getErrorInfo (json) {
    let errorMessages = [];
    let totalErrors = {};

    (json?.errors || []).forEach(err => {
      errorMessages.push(err.message);
      const extensionResponse = err.extensions?.response || {};
      const status = extensionResponse.status || err.extensions?.code || 'Unknown Server Error';
      totalErrors[status] = totalErrors[status] || [];
      totalErrors[status].push({
        error: err.message,
        path: err.path && err.path.join('.')
      });
      this.metrics.error(`${REQ_ERR_MSG} Message: ${err.message}`, err, {requestId: json.reqId});
    });

    return {
      description: errorMessages.join(',') || 'Unknown GQL Error',
      totalErrors
    };
  }

  async getGqlResponse(res) {
    let errorDescription = '';
    let totalErrors = {};
    let errorInfo = {};
    const reqId = (res?.headers && res.headers.get('x-request-id')) || 'n/a';

    if (res && !res.ok) {
      try {
        const errorJson = await res.json();
        // Form the error message object based off whether it is an io error or not
        if (errorJson?.error_code) {
          errorDescription = `${errorJson.message} (${errorJson.error_code})`;
          totalErrors = {[res.status]: errorDescription};
        } else {
          ({description: errorDescription, totalErrors} = this.getErrorInfo(errorJson));
        }
      } catch {
        errorDescription = 'Unknown GQL Error.';
      }

      let e = new Error(`${res.status} - ${errorDescription}`);
      e.requestId = reqId;
      e.status = res.status;
      e.errors = totalErrors;
      throw e;
    }

    const json = await res.json() || {};

    // Handling inconsistent 401s from the gql response layer
    // If we get > 1, we want to throw an error. This is due to adobe.io caching.
    errorInfo = this.getErrorInfo(json);
    if (Object.keys(errorInfo.totalErrors).length) {
      let e = new Error(`Non-2xx response from backing services in gql: ${errorInfo.description}`);
      e.errors = errorInfo.totalErrors;
      e.requestId = reqId;
      throw e;
    }

    return json;
  }

  async request(query, variables, scope) {
    let res = await queryApi({data: {query, variables}, maxRetries: 5, scope, totalFetchTime: 10000});
    return await this.getGqlResponse(res);
  }

  shellDataHandler(query, {debugKey, scope = FetchScope.ORG, queryName = 'GraphQl'}) {
    return async variables => {
      const gqlTimer = this.metrics.start(queryName);
      try {
        const response = await this.request(query, variables || {}, scope);
        const data = response.data;
        if (data) {
          gqlTimer.time('data.initialized', `Initialized Shell Data from GQL required for ${debugKey}.`, {requestId: response.reqID});
          this.metrics.info(`Initialized Shell Data required for ${debugKey} using GQL.`,
            {requestId: response.reqID});
        } else {
          this.metrics.error(`GQL returned invalid/null response for ${debugKey} Data`);
        }
        return data;
      } catch (error) {
        gqlTimer.time(
          'failure',
          `Failed to initialize Shell App Data for ${debugKey} using GQL. Error: ${error.message}`,
          {level: Level.ERROR}
        );
        throw error;
      }
    };
  }
}
